import React from 'react';

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        console.error('Error captured in getDerivedStateFromError:', error);
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log the error to console or error reporting service
        console.error('Error caught by boundary:', error, errorInfo);
        this.setState({
            error,
            errorInfo
        });

        // In production, you could send to an error tracking service like Sentry
        // if (import.meta.env.PROD) {
        //     // Send to error tracking service
        //     Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
        // }
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            const isDevelopment = import.meta.env.DEV;

            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                    <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
                        {isDevelopment && (
                            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                <p className="text-sm text-yellow-800">
                                    <strong>Development Mode:</strong> Close the Vite error overlay to see this error boundary.
                                </p>
                            </div>
                        )}
                        
                        <div className="flex items-center mb-6">
                            <svg
                                className="w-12 h-12 text-red-500 mr-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Something went wrong
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    An unexpected error occurred in the application
                                </p>
                            </div>
                        </div>

                        {this.state.error && (
                            <div className="mb-6">
                                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                                    <h3 className="font-semibold text-red-800 mb-2">Error Details:</h3>
                                    <p className="text-red-700 font-mono text-sm break-all">
                                        {this.state.error.toString()}
                                    </p>
                                </div>

                                {this.state.errorInfo && isDevelopment && (
                                    <details className="bg-gray-50 border border-gray-200 rounded-md p-4">
                                        <summary className="font-semibold text-gray-700 cursor-pointer">
                                            Stack Trace (click to expand)
                                        </summary>
                                        <pre className="mt-2 text-xs text-gray-600 overflow-auto max-h-64">
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        )}

                        <div className="flex gap-4">
                            <button
                                onClick={this.handleReload}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-md transition-colors"
                            >
                                Reload Application
                            </button>
                            <button
                                onClick={this.handleReset}
                                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-md transition-colors"
                            >
                                Try Again
                            </button>
                        </div>

                        <p className="text-sm text-gray-500 mt-6 text-center">
                            {isDevelopment 
                                ? "Check the console for more detailed error information."
                                : "If this problem persists, please contact support with the error details above."
                            }
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
